import { shortUuid } from '@tunasong/models'
import { MelodyLib } from '@tunasong/music-lib'
import type { Melody, MelodyChordLane, Scale } from '@tunasong/schemas'
import { Button } from '@tunasong/ui-lib'
import { useRef, useState } from 'react'
import type { CSSProperties, FC, KeyboardEvent, UIEvent } from 'react'
import { Keyboard } from '../keyboard/keyboard.js'
import type { KeyboardNoteInfo } from '../keyboard/keyboard.js'
import { MelodyCapture } from '../melody-capture/melody-capture.js'
import { MelodyPracticeMenu } from '../pitch-trainer-phrases/melody-practice-menu.js'
import { TheoryGlyphs } from '../theory-copy/theory-glyphs.js'
import { useTheoryLevel } from '../theory-copy/use-theory-copy.js'
import { ChordLane, ChordLaneGuess } from './chord-lane.js'
import { midiInsert } from './melody-midi-insert.js'
import styles from './melody-editor.module.css'
import { MelodyRoll } from './melody-roll.js'
import { rollBeats, stepToBeat } from './melody-roll-geometry.js'
import { MelodyShortcutsHelp } from './melody-shortcuts-help.js'
import { MelodyTransport } from './melody-transport.js'
import { TakesList } from './takes-list.js'
import { usePrefersReducedMotion } from './use-prefers-reduced-motion.js'
import { MelodyRollHeader } from './melody-roll-header.js'
import { useMelodyMidiInsert } from './use-melody-midi-insert.js'
import { useMelodyRollPlayback } from './use-melody-roll-playback.js'
import { VelocityLane } from './velocity-lane.js'

/**
 * The full melody editor: header, roll, velocity lane, on-screen keyboard, MIDI insert, transport.
 *
 * It owns exactly three pieces of state, none of which belong to the melody: the insert cursor,
 * the selected note, and the "quantized" A/B toggle. Everything else is a pure `MelodyLib` op
 * against the melody the caller passed in, returned through `onChange` - so the entity is the one
 * copy of the truth and there is no local draft to reconcile.
 *
 * The invariant readout is deliberately visible rather than silently repaired: a melody arriving
 * off-grid means something upstream (a converter, or one day the §5 quantizer) is wrong, and
 * quietly snapping it would hide that.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §4
 */

export interface MelodyEditorProps {
  melody: Melody
  onChange: (melody: Melody) => void
  /**
   * Where a capture's recording is filed - the melody's own entity, or the document an embedded
   * melody sits in. Absent means the take's audio is saved without a parent.
   */
  captureParentId?: string
  /** What the Practice menu's phrases are named after; defaults to the melody's name */
  practiceName?: string
  /**
   * The section's chart chords, which the chord lane follows; null outside a section, and absent where
   * there is no document at all. Either way the lane is then the melody's own (melody toolkit plan M1).
   */
  chart?: MelodyLib.ChartChord[] | null
  /** The song's or the section's key: the drone's, when the melody has none of its own (spec §7, §8) */
  songKey?: Scale
  className?: string
}

/** Typing in these is typing, never a shortcut */
const isTextEntry = (target: EventTarget) =>
  target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement

/** The keys `handlePlotKeyDown` answers to, as `aria-keyshortcuts` names them */
const plotKeyShortcuts = 'Space L C M R Escape'

export const MelodyEditor: FC<MelodyEditorProps> = props => {
  const { melody, onChange, captureParentId, practiceName, chart, songKey, className } = props

  const rootRef = useRef<HTMLDivElement>(null)
  const [cursorBeats, setCursorBeats] = useState(0)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [quantized, setQuantized] = useState(false)
  const [capturing, setCapturing] = useState(false)
  const laneTrackRef = useRef<HTMLDivElement>(null)
  const rollScrollerRef = useRef<HTMLDivElement>(null)
  const velocityScrollerRef = useRef<HTMLDivElement>(null)
  /** Spec §5.1: Chords and Melody both on to start with; Loop and the drone off */
  const [isChordsOn, setChordsOn] = useState(true)
  const [isMelodyOn, setMelodyOn] = useState(true)
  const [isLooping, setLooping] = useState(false)
  const [isDroneOn, setDroneOn] = useState(false)

  /** The lane is resolved on every render, so a chord typed into the chart shows up at once (§8) */
  const lane = MelodyLib.resolveChordLane(chart ?? null, melody.chordLane, melody.tempo.meter)
  const hasChords = lane.chords.length > 0
  /** The key the drone plays in and the cues are read in: the melody's own, else the section's or song's */
  const droneKey = melody.key ?? songKey

  /**
   * Theory cues (melody toolkit M3; spec §5.3), at the user's Theory cues level: off hides them, and
   * Advanced adds each note's degree number. Deterministic and offline.
   */
  const theoryLevel = useTheoryLevel()
  const isCueing = theoryLevel !== 'off'
  const roles = isCueing ? MelodyLib.noteRoles(melody, lane, droneKey) : []
  const whyLines =
    selectedIndex === null || theoryLevel === 'off'
      ? []
      : roles
          .filter(role => role.noteIndex === selectedIndex)
          .map(role => MelodyLib.whyLine(role, { level: theoryLevel, ...(droneKey ? { key: droneKey } : {}) }))
  /** Spec §8: with no chords and no key there is nothing to read the notes against */
  const isMissingKey = isCueing && !hasChords && !droneKey
  const totalBeats = rollBeats(melody, MelodyLib.laneEnd(lane))
  const handleLaneChange = (chordLane: MelodyChordLane) => onChange({ ...melody, chordLane })

  const playback = useMelodyRollPlayback({
    melody,
    quantized,
    lane,
    /** With no chords, the Chords toggle has nothing to play: the drone, when it is on, stands in */
    isChordsOn: hasChords ? isChordsOn : isDroneOn,
    isMelodyOn,
    isLooping,
    ...(droneKey ? { drone: droneKey } : {}),
  })
  const isReducedMotion = usePrefersReducedMotion()
  const playheadMs = isReducedMotion ? stepToBeat(playback.playheadMs, melody.tempo.bpm) : playback.playheadMs

  useMelodyMidiInsert({ melody, cursorBeats, onChange, onCursorChange: setCursorBeats })

  const handleKeyboardNote = ({ midiNumber }: KeyboardNoteInfo) => {
    /* An on-screen key has no velocity, so it takes the same default a converted suggestion does. */
    const result = midiInsert(melody, cursorBeats, midiNumber, MelodyLib.SUGGESTION_VELOCITY)
    onChange(result.melody)
    setCursorBeats(result.cursorBeats)
  }

  const handleChordsChange = (isOn: boolean) => (hasChords ? setChordsOn(isOn) : setDroneOn(isOn))

  /**
   * Spec §10: the roll's shortcuts, active only while focus is in the roll and its lanes. A modifier held
   * means the browser's shortcut, and a text field is typing.
   */
  const handlePlotKeyDown = (event: KeyboardEvent) => {
    /** A button keeps its own keys: Space presses it (Joni, M1–M5 review) */
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.altKey ||
      isTextEntry(event.target) ||
      event.target instanceof HTMLButtonElement
    ) {
      return
    }
    const actions: Record<string, () => void> = {
      ' ': () => (playback.isPlaying ? playback.stop() : playback.play()),
      l: () => setLooping(!isLooping),
      c: () => handleChordsChange(!(hasChords ? isChordsOn : isDroneOn)),
      m: () => setMelodyOn(!isMelodyOn),
      r: () => setCapturing(true),
      Escape: () => rootRef.current?.focus(),
    }
    const action = actions[event.key.length === 1 ? event.key.toLowerCase() : event.key]
    if (!action) {
      return
    }
    event.preventDefault()
    action()
  }

  const issues = MelodyLib.validateMelody(melody)

  const rollBars = totalBeats / MelodyLib.barBeats(melody.tempo.meter)
  /** The sticky lane has no scrollbar of its own: it shows the beats the roll scrolled to */
  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const { scrollLeft } = event.currentTarget
    if (laneTrackRef.current) {
      laneTrackRef.current.scrollLeft = scrollLeft
    }
    if (velocityScrollerRef.current && velocityScrollerRef.current.scrollLeft !== scrollLeft) {
      velocityScrollerRef.current.scrollLeft = scrollLeft
    }
  }

  /** The velocity lane scrolls on its own too (a finger pans it): the roll, and so the lane, follow */
  const handleVelocityScroll = (event: UIEvent<HTMLDivElement>) => {
    const { scrollLeft } = event.currentTarget
    if (rollScrollerRef.current && rollScrollerRef.current.scrollLeft !== scrollLeft) {
      rollScrollerRef.current.scrollLeft = scrollLeft
    }
  }

  return (
    <div ref={rootRef} tabIndex={-1} className={className ? `${styles.root} ${className}` : styles.root}>
      <MelodyRollHeader melody={melody} onChange={onChange} quantized={quantized} onQuantizedChange={setQuantized} />

      {/* Spec §5.5: the takes, under the header. Each change is one whole melody, so one undo step */}
      <TakesList
        takes={melody.takes ?? []}
        {...(melody.currentTakeId ? { currentTakeId: melody.currentTakeId } : {})}
        onAudition={id => {
          const take = melody.takes?.find(entry => entry.id === id)
          if (take) {
            playback.playMelody({ ...melody, notes: take.notes })
          }
        }}
        onUse={id => onChange(MelodyLib.switchToTake(melody, id, shortUuid()))}
        onRename={(id, name) => onChange(MelodyLib.renameTake(melody, id, name))}
        onSaveAsTake={() => onChange(MelodyLib.saveAsTake(melody, shortUuid()))}
      />

      <div className={styles.bar}>
        <MelodyTransport
          className={styles.transportBar}
          isPlaying={playback.isPlaying}
          onPlay={playback.play}
          onStop={playback.stop}
          isLooping={isLooping}
          onLoopChange={setLooping}
          bpm={melody.tempo.bpm}
          hasNotes={melody.notes.length > 0}
          hasChords={hasChords}
          isChordsOn={isChordsOn}
          onChordsChange={setChordsOn}
          isMelodyOn={isMelodyOn}
          onMelodyChange={setMelodyOn}
          onSingIdea={() => setCapturing(true)}
        />
        <MelodyShortcutsHelp />
      </div>

      <div className={styles.transport}>
        <span className={styles.cursor}>{`Insert at beat ${cursorBeats + 1}`}</span>
        <Button onClick={() => setCursorBeats(0)}>Cursor to start</Button>
        {/*
         * Practice sits here rather than in `MelodyRollHeader`: that header holds the melody's FIELDS, and
         * a control that neither reads nor writes one does not belong there.
         */}
        {melody.notes.length > 0 ? (
          <MelodyPracticeMenu melody={melody} sectionName={practiceName ?? melody.name ?? 'Melody'} />
        ) : null}
      </div>

      {/* Spec §5.2: Sing an idea is the capture, sung over the lane's chords (or the drone) */}
      <MelodyCapture
        melody={melody}
        open={capturing}
        parentId={captureParentId}
        /* Plan D2: a capture lands as notes and as a new take, and the notes there were are kept */
        onCapture={captured =>
          onChange(MelodyLib.addCaptureTake(melody, captured, { kept: shortUuid(), capture: shortUuid() }))
        }
        onClose={() => setCapturing(false)}
        {...(hasChords ? { backing: { lane } } : isDroneOn && droneKey ? { backing: { lane, drone: droneKey } } : {})}
      />

      {issues.length > 0 ? (
        <ul className={styles.issues} role="status">
          {issues.map(issue => (
            <li key={`${issue.kind}-${issue.noteIndex}`}>{issue.message}</li>
          ))}
        </ul>
      ) : null}

      {/*
       * Spec §9: below 600px the roll scrolls sideways, each bar wide enough for a 44px chord button
       * beside its edge handle, and the chord lane, roll and velocity lane scroll together. The lane sits
       * outside the scroller, whose overflow would hold it, so it sticks to the page while the roll scrolls
       * under it; it follows the scroller sideways (Joni, M1–M5 review).
       */}
      {/* Outside the lane's sideways scroll, so it never scrolls out of view on a phone (Joni) */}
      <ChordLaneGuess lane={lane} onChange={handleLaneChange} />

      {/* The roll's shortcuts work with focus anywhere in the lane, the roll or the velocity lane (§10) */}
      <div className={styles.plotArea} onKeyDown={handlePlotKeyDown}>
        {/* What the lane sticks within: the lane and the roll only, so it leaves with the roll (Joni) */}
        <div className={styles.stickArea}>
          <div ref={laneTrackRef} className={styles.laneTrack} style={{ '--roll-bars': rollBars } as CSSProperties}>
            <ChordLane
              className={styles.plotWidth}
              lane={lane}
              meter={melody.tempo.meter}
              totalBeats={totalBeats}
              isInSection={Array.isArray(chart)}
              onChange={handleLaneChange}
              {...(droneKey ? { drone: { isOn: isDroneOn, onChange: setDroneOn } } : {})}
            />
          </div>

          <div ref={rollScrollerRef} className={styles.scroller} onScroll={handleScroll}>
            <div className={`${styles.plot} ${styles.plotWidth}`} style={{ '--roll-bars': rollBars } as CSSProperties}>
              <MelodyRoll
                melody={melody}
                onChange={onChange}
                quantized={quantized}
                playheadMs={playheadMs}
                cursorBeats={cursorBeats}
                onCursorChange={setCursorBeats}
                selectedIndex={selectedIndex}
                onSelect={setSelectedIndex}
                minBeats={totalBeats}
                keyShortcuts={plotKeyShortcuts}
                roles={roles}
                {...(droneKey ? { cueKey: droneKey } : {})}
                isShadingStrongBeats={isCueing}
                isShowingDegreeNumbers={theoryLevel === 'advanced'}
                {...(isCueing && hasChords
                  ? { rowFits: (beat: number) => MelodyLib.fitsAt(lane, droneKey, beat) }
                  : {})}
              />
            </div>
          </div>
        </div>

        <div ref={velocityScrollerRef} className={styles.scroller} onScroll={handleVelocityScroll}>
          <div className={`${styles.plot} ${styles.plotWidth}`} style={{ '--roll-bars': rollBars } as CSSProperties}>
            <VelocityLane
              melody={melody}
              onChange={onChange}
              selectedIndex={selectedIndex}
              onSelect={setSelectedIndex}
              minBeats={totalBeats}
            />
          </div>
        </div>
      </div>

      {/* Spec §5.3: the why line, for the selected or focused note; one sentence per chord it is judged against */}
      {isMissingKey ? (
        <p className={styles.whyLine}>Set a key to see how notes relate.</p>
      ) : whyLines.length > 0 ? (
        <p className={styles.whyLine} data-testid="melody-why-line">
          {/* Each ♭ and ♯ announced as a word (Brian, 2026-10-23) */}
          <TheoryGlyphs>{whyLines.join(' ')}</TheoryGlyphs>
        </p>
      ) : null}

      <div className={styles.keyboard} data-testid="melody-keyboard">
        <Keyboard scale={melody.key ?? null} onPlayNote={handleKeyboardNote} />
      </div>
    </div>
  )
}
